import React, { useEffect, useRef } from 'react';

interface AdSenseAdProps {
  adSlot: string;
  adClient: string;
  adFormat?: 'auto' | 'rectangle' | 'vertical' | 'horizontal';
  adLayout?: string;
  adLayoutKey?: string;
  style?: React.CSSProperties;
  className?: string;
  fullWidthResponsive?: boolean;
}

const debugLog = (value: any): void => console.log(value);

const AdSenseAd: React.FC<AdSenseAdProps> = ({
  adSlot,
  adClient,
  adFormat = 'auto',
  adLayout,
  adLayoutKey,
  style,
  className,
  fullWidthResponsive = true,
}) => {
  const adRef = useRef<HTMLModElement>(null);
  const isAdPushed = useRef(false);

  useEffect(() => {
    // Only push the ad once when component mounts and script is loaded
    // Use requestIdleCallback to defer ad push and prevent blocking rendering
    let idleCallbackId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    
    const pushAd = () => {
      if (!isAdPushed.current && window.adsbygoogle && adRef.current) {
        // Check if this specific ad element is already initialized
        // The status is set directly on the ins element, not a child
        if (adRef.current.getAttribute('data-adsbygoogle-status')) {
          debugLog('Ad already initialized, skipping');
          isAdPushed.current = true;
          return;
        }

        try {
          debugLog(`Pushing ad to adsbygoogle - slot: ${adSlot}`);
          // Wrap in try-catch to handle potential 403 or network errors gracefully
          window.adsbygoogle.push({});
          isAdPushed.current = true;
        } catch (error) {
          console.error(`AdSense error for slot ${adSlot}:`, error);
          // Don't retry on error to prevent infinite loops
          isAdPushed.current = true;
        }
      }
    };

    const schedulePush = () => {
      // Check if user is currently scrolling - if so, delay further
      const isScrolling = document.body.getAttribute('data-scrolling') === 'true';
      
      if (isScrolling) {
        // Wait until scrolling stops
        timeoutId = setTimeout(schedulePush, 300) as any;
        return;
      }

      // Defer ad push to avoid blocking critical rendering
      if ('requestIdleCallback' in window) {
        idleCallbackId = (window as any).requestIdleCallback(pushAd, { timeout: 2000 });
      } else {
        // Fallback for browsers without requestIdleCallback
        timeoutId = setTimeout(pushAd, 100) as any;
      }
    };

    schedulePush();

    return () => {
      if (idleCallbackId && 'cancelIdleCallback' in window) {
        (window as any).cancelIdleCallback(idleCallbackId);
      }
      if (timeoutId) {
        clearTimeout(timeoutId as any);
      }
    };
  }, [adSlot]);

  return (
    <div className={className} style={style}>
      <ins
        ref={adRef}
        className="adsbygoogle"
        style={{
          display: 'block',
          ...style,
        }}
        data-ad-client={adClient}
        data-ad-slot={adSlot}
        data-ad-format={adFormat}
        data-ad-layout={adLayout}
        data-ad-layout-key={adLayoutKey}
        data-full-width-responsive={fullWidthResponsive}
      />
    </div>
  );
};

export default AdSenseAd;
